import { useQuery } from '@tanstack/react-query';
import { allTariffs } from '@/enteties/tariff/api';
import { useOnError } from 'shared/hooks/useOnError';
import { useMemo } from 'react';
import _ from 'lodash';
import type { Control, FieldValues, Path } from 'react-hook-form';
import type { IServiceSelect } from 'widgets/project-creation-stepper/model';
import { TariffSelector, type TariffSelectorProps } from './tariff-selector';
import type { Tariff } from '@/enteties/tariff/modal';
interface WithTariffDataProps<TData extends FieldValues> {
  name: Path<TData>;
  control: Control<TData>;
  serviceType: IServiceSelect['name'];
}
export const withTariffData = <TData extends FieldValues,>(WrappedComponent: React.ComponentType<TariffSelectorProps<TData>>) => {
  const ComponentWithTariffData = ({
    serviceType,
    ...props
  }: WithTariffDataProps<TData>) => {
    const query = allTariffs.getAllTariffsQueryOptions();
    const {
      data,
      isLoading,
      isError,
      error
    } = useQuery(query);
    useOnError({
      isError,
      description: error?.message
    });
    const tariffsData = useMemo(() => {
      if (!data) return [];
      if (serviceType === 'marketplace' || serviceType === 'db') {
        const tariffsClone = _.cloneDeep(data);
        return tariffsClone.filter((tariff: Tariff) => tariff.id !== 4);
      }
      return data;
    }, [serviceType, data]);
    return <WrappedComponent {...props as TariffSelectorProps<TData>} tariffsData={tariffsData} isLoading={isLoading} data-sentry-element="WrappedComponent" data-sentry-component="ComponentWithTariffData" data-sentry-source-file="with-tariff-data.tsx" />;
  };
  ComponentWithTariffData.displayName = `WithTariffData(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return ComponentWithTariffData;
};
export const TariffSelectorWithAllTariffs: <TData extends FieldValues>(props: WithTariffDataProps<TData>) => React.JSX.Element = withTariffData(TariffSelector as React.ComponentType<TariffSelectorProps<any>>);